import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

import {
  AssessmentPeriodActionPlanTaskRead,
  AssessmentPeriodActionPlanTaskStatusEnum,
} from '../../../store/generated/portal/assessments/plans-team.js';

import { actionPlanApi } from 'areas/recommendations/api/actionPlanApi';
import { actionPlanTaskApi } from 'areas/recommendations/api/actionPlanTaskApi';

import {
  HeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from 'common/components/Table';
import {
  StyledCancelButton,
  StyledDeleteButton,
  StyledSaveButton,
} from 'common/components/button/StyledButton';
import { PageHeader, PageSection } from 'common/styles/page';
import { useNavigate } from 'react-router';
import { Button, Icon, Modal, Popup } from 'semantic-ui-react';
import PositionedModal from 'common/components/PositionedModal';
import { ResourceListModal } from 'areas/settings/repository-management/components/ResourceListModal';
import { StatusIcon } from 'common/components/StatusIcon';
import { useToasts } from 'common/toast';
import TaskCompletionCircle from 'common/components/TaskCompletionCircle';
import {
  isCurrentUserStaffOrSuperAdmin,
  getOrganizationMembers,
} from 'common/selectors/auth';
import { tableCellColor } from 'common/constants/globalstyles';
import { StyledDateInput } from '../../../common/components/DateInput';

export const ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS = [
  { id: 1, label: 'Not Started' },
  { id: 2, label: 'In Progress' },
  { id: 3, label: 'Pending' },
  { id: 4, label: 'Deferred' },
  { id: 5, label: 'Completed' },
  { id: 6, label: 'Incomplete' },
  { id: 7, label: 'Not Applicable' },
] as const;

interface Task {
  id: number;
  phase_id: number;
  activity_name: string;
  task_name: string;
  metric: string;
  status: AssessmentPeriodActionPlanTaskStatusEnum;
  owner: number;
  start_date: string;
  end_date: string;
  notes: string;
  activity_sort_order: number;
  task_sort_order: number;
  content: {
    id: number;
    name: string;
    type: number;
    object_key: string;
  }[];
}

interface FoundationActionPlanProps {
  title: string;
  description: string;
  taskData: any[];
  actionPlanId: number;
}

const FoundationActionPlan: React.FC<FoundationActionPlanProps> = ({
  title,
  description,
  taskData,
  actionPlanId,
}) => {
  const [updateActionPlan] = actionPlanTaskApi.useApiPortalV1AssessmentsPlansTasksPartialUpdateMutation(
    {}
  );
  const [deleteActionPlan] = actionPlanApi.useApiPortalV1AssessmentsPlansOrganizationDestroyMutation();

  const isUserAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const canManagePlan = isUserAdmin;

  const [completed, setCompleted] = useState([0, 0, 0]);

  const [isResourceListOpen, setResourceListOpen] = React.useState(false);
  const [resources, setResources] = useState<
    {
      id: number;
      name: string;
      type: number;
      object_key: string;
    }[]
  >([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const cancelClickedRef = React.useRef(false);
  const savingRef = React.useRef(false);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState<boolean>(true);

  const [selectedSubTab, setSelectedSubTab] = useState(0);

  const [editedTaskData, setEditedTaskData] = useState<
    Record<
      number,
      {
        owner?: number | null;
        status?: AssessmentPeriodActionPlanTaskStatusEnum;
        start_date?: string | null;
        end_date?: string | null;
        notes?: string | null;
        content?: {
          id: number;
          name: string;
          type: number;
        }[];
      }
    >
  >({});

  const members = useSelector(getOrganizationMembers);

  const toggleSection = (activityName: string) => {
    setOpenSections(prev => ({
      ...prev,
      [activityName]: !prev[activityName],
    }));
  };

  const toggleAllSections = () => {
    const newExpanded = !allExpanded;
    const newOpenSections = Object.keys(groupedData).reduce(
      (acc, key) => {
        acc[key] = newExpanded;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setOpenSections(newOpenSections);
    setAllExpanded(newExpanded);
  };

  const getSectionCompletion = (tasks: Task[]): number => {
    const total = tasks.length;
    const completed = tasks.filter(task =>
      [5, 6, 7].includes(editedTaskData[task.id]?.status ?? task.status)
    ).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  function useBlocker(
    blocker: () => boolean,
    when: boolean,
    onBlocked?: (args: any[], perform?: () => void) => void
  ) {
    const navigator = useContext(NavigationContext).navigator;

    useEffect(() => {
      if (!when) return;

      const push = navigator.push;

      navigator.push = (...args: [string, any?]) => {
        if (blocker()) {
          // Instead of a sync confirm, call the onBlocked callback so the
          // caller can display a styled modal and decide later whether to
          // allow navigation. Provide a `perform` function that will execute
          // the original push when the user confirms.
          const perform = () => push(...args);
          if (onBlocked) {
            onBlocked(args, perform);
            return;
          }

          // Fallback to the old behavior if no handler is provided.
          const confirm = window.confirm(
            'You have unsaved changes. Are you sure you want to leave?'
          );
          if (!confirm) return;
          perform();
          return;
        }
        push(...args);
      };

      return () => {
        navigator.push = push;
      };
    }, [blocker, when, navigator, onBlocked]);
  }

  // Modal-based blocker: when a navigation attempt happens while there
  // are unsaved changes, show a nicer `PositionedModal` instead of the
  // native `window.confirm` dialog.
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const pendingPerformRef = React.useRef<(() => void) | null>(null);

  const handleBlocked = (args: any[], perform?: () => void) => {
    // store the perform callback so the confirm button can execute it
    pendingPerformRef.current = perform ?? null;
    setUnsavedModalOpen(true);
  };

  useBlocker(
    () => !cancelClickedRef.current && !savingRef.current,
    Object.keys(editedTaskData).length > 0,
    handleBlocked
  );

  const handleChange = (taskId: number, field: string, value: any) => {
    setEditedTaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const handleStatusIconClick = (taskId: number, currentStatus: number) => {
    const statusIds = ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS.map(s => s.id);
    const currentIndex = statusIds.indexOf(
      currentStatus as AssessmentPeriodActionPlanTaskStatusEnum
    );
    let nextIndex = currentIndex + 1;

    // if anything other than completed, set to completed, else set to not started
    if (currentIndex !== 4) {
      nextIndex = 4;
    } else {
      nextIndex = 0;
    }
    const nextStatus = statusIds[nextIndex];
    handleChange(taskId, 'status', nextStatus);
  };

  // Utility function to group tasks
  const groupByActivityName = (data: Task[], phase: number) =>
    data
      .filter(task => task.phase_id === phase + 1) // Filter tasks by the given phase
      .sort((a, b) => a.activity_sort_order - b.activity_sort_order) // Sort activity_sort_order
      .sort((a, b) => a.task_sort_order - b.task_sort_order) // Sort tasks by task_sort_order
      .reduce<Record<string, Task[]>>((acc, task) => {
        if (!acc[task.activity_name]) {
          acc[task.activity_name] = [];
        }
        acc[task.activity_name].push(task);
        return acc;
      }, {});

  const groupedData = useMemo(
    () => groupByActivityName(taskData, selectedSubTab),
    [taskData, selectedSubTab]
  );
  const allTasks = useMemo(() => Object.values(groupedData).flat(), [groupedData]);

  const computeCompletedPercent = (data: Task[], phase: number) => {
    const phaseTasks = data.filter(task => task.phase_id === phase);

    // Count the total tasks and tasks with status 5, 6, or 7
    const totalTasks = phaseTasks.length;
    const completedTasks = phaseTasks.filter(task => [5, 6, 7].includes(task.status)).length;

    // Compute the percentage of completed tasks
    const completedPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    return completedPercent;
  };

  useEffect(() => {
    setCompleted([
      computeCompletedPercent(taskData, 1),
      computeCompletedPercent(taskData, 2),
      computeCompletedPercent(taskData, 3),
    ]);
  }, [taskData]);

  // Initialize all sections as open when data loads
  useEffect(() => {
    setOpenSections(prev => {
      const newSections = { ...prev };
      Object.keys(groupedData).forEach(key => {
        if (!(key in newSections)) {
          newSections[key] = true;
        }
      });
      return newSections;
    });
  }, [groupedData]);

  // Save Project Plan
  const onSave = async () => {
    if (Object.keys(editedTaskData).length === 0) return; // No changes to save

    savingRef.current = true;

    try {
      const updatePromises = Object.entries(editedTaskData).map(([taskId, updatedFields]) => {
        const { status, owner, start_date, end_date, notes } = updatedFields;
        const payload: Partial<AssessmentPeriodActionPlanTaskRead> = {};

        if (status !== undefined) {
          payload.status = status;
        }
        if (owner !== undefined) {
          payload.owner = owner;
        }
        if (start_date !== undefined) {
          payload.start_date = start_date ? new Date(start_date).toISOString().split('T')[0] : null;
        }
        if (end_date !== undefined) {
          payload.end_date = end_date ? new Date(end_date).toISOString().split('T')[0] : null;
        }

        if (notes !== undefined) {
          payload.notes = notes;
        }

        if (Object.keys(payload).length > 0) {
          return updateActionPlan({
            id: parseInt(taskId, 10),
            patchedAssessmentPeriodActionPlanTask: payload,
          }).unwrap();
        }

        return Promise.resolve(); // No-op for tasks with no changes
      });
      await Promise.all(updatePromises);
      dispatch(actionPlanApi.util.resetApiState());

      setEditedTaskData({});
      addToast('Changes saved successfully', {
        appearance: 'success',
        autoDismiss: true,
      });

      navigate(`/app/foundations/`);
    } catch (error) {
      console.error('Error saving tasks:', error);
    } finally {
      savingRef.current = false; // Reset in case of error
    }
  };

  const handleViewClick = (
    resources: {
      id: number;
      name: string;
      type: number;
      object_key: string;
    }[]
  ) => {
    setResourceListOpen(true);
    setResources(resources);
  };

  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const openNotesModal = (taskId: number) => {
    setActiveTaskId(taskId);
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setActiveTaskId(null);
  };

  return (
    <>
      <PageSection padbottom padleft="10px">
        <PageHeader
          title={title}
          caption={description}
          controls={
            <div style={{ display: 'flex', gap: '10px' }}>
              <StyledSaveButton
                color={Object.keys(editedTaskData).length === 0 ? 'gray' : 'green'}
                label="Save"
                width="90px"
                onClick={onSave}
              />
              <StyledCancelButton
                label="Cancel"
                width="90px"
                onClick={() => {
                  cancelClickedRef.current = true;
                  navigate(`/app/foundations/`);
                }}
              />
            </div>
          }
        />
      </PageSection>

      <Table>
        <TableHeader>
          <HeaderCell width={5}>Task</HeaderCell>
          <HeaderCell width={3}>Metric</HeaderCell>
          <HeaderCell width={2}>Status</HeaderCell>
          <HeaderCell width={2}>Owner</HeaderCell>
          <HeaderCell width={1}>Start</HeaderCell>
          <HeaderCell width={1}>End</HeaderCell>
          <HeaderCell width={2}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'end' }}
            >
              <Icon
                name={allExpanded ? 'minus square outline' : 'plus square outline'}
                style={{ cursor: 'pointer' }}
                onClick={toggleAllSections}
                size="large"
                color="grey"
              />
            </div>
          </HeaderCell>
        </TableHeader>
        <TableBody>
          {Object.entries(groupedData).map(([activityName, tasks]) => (
            <React.Fragment key={activityName}>
              <TableRow selectable="true" onClick={() => toggleSection(activityName)}>
                <TableCell colSpan={7} backgroundColor="#f3f3f3">
                  <DividerTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TaskCompletionCircle percent={getSectionCompletion(tasks)} size={20} />
                      {activityName}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon color="grey" name={openSections[activityName] ? 'minus' : 'plus'} />
                    </div>
                  </DividerTitle>
                </TableCell>
              </TableRow>

              {openSections[activityName] &&
                tasks.map(task => (
                  <TableRow key={task.id}>
                    <TableCell>
                      <StatusIcon
                        status={editedTaskData[task.id]?.status ?? task.status}
                        size="large"
                        onClick={() =>
                          handleStatusIconClick(
                            task.id,
                            editedTaskData[task.id]?.status ?? task.status
                          )
                        }
                      />{' '}
                      {task.task_name}
                    </TableCell>
                    <TableCell>{task.metric}</TableCell>
                    <TableCell>
                      <Select
                        value={editedTaskData[task.id]?.status || task.status}
                        onChange={e =>
                          handleChange(
                            task.id,
                            'status',
                            Number(e.target.value) as AssessmentPeriodActionPlanTaskStatusEnum
                          )
                        }
                      >
                        {ASSESSMENT_PERIOD_ACTION_PLAN_TASK_STATUS.map(status => (
                          <option key={status.label} value={status.id}>
                            {status.label}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={
                          editedTaskData[task.id]?.owner !== undefined
                            ? String(editedTaskData[task.id]?.owner ?? '')
                            : String(task.owner ?? '')
                        }
                        onChange={e => {
                          const value = e.target.value;
                          handleChange(task.id, 'owner', value === '' ? null : parseInt(value, 10));
                        }}
                      >
                        <option value="">-</option>
                        {[...members].map(member => (
                          <option key={member.id} value={member.id}>
                            {(member.firstName || member.lastName) ? `${member.lastName}${member.firstName ? `, ${member.firstName}` : ''}` : ''}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <StyledDateInput
                        value={
                          editedTaskData?.[task.id]?.start_date !== undefined
                            ? (editedTaskData[task.id]?.start_date ?? null)
                            : (task.start_date ?? null)
                        }
                        onChange={val => handleChange(task.id, 'start_date', val)}
                        hasValue={!!(editedTaskData?.[task.id]?.start_date || task.start_date)}
                      />
                    </TableCell>
                    <TableCell>
                      <StyledDateInput
                        value={
                          editedTaskData?.[task.id]?.end_date !== undefined
                            ? (editedTaskData[task.id]?.end_date ?? null)
                            : (task.end_date ?? null)
                        }
                        onChange={val => handleChange(task.id, 'end_date', val)}
                        hasValue={!!(editedTaskData?.[task.id]?.end_date || task.end_date)}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const note =
                          editedTaskData[task.id]?.notes !== undefined
                            ? editedTaskData[task.id]?.notes
                            : (task.notes ?? '');

                        return note ? (
                          <Popup
                            content={note}
                            position="top center"
                            trigger={
                              <Button
                                icon="edit"
                                style={{
                                  backgroundColor: 'rgba(129, 199, 132, 0.5)',
                                  border: '1px solid rgba(129, 199, 132, 1)', // Optional border for clarity
                                }}
                                onClick={() => openNotesModal(task.id)}
                              />
                            }
                          />
                        ) : (
                          <Button icon="edit" onClick={() => openNotesModal(task.id)} />
                        );
                      })()}

                      <Button
                        icon="television"
                        disabled={task.content.length === 0}
                        onClick={() => handleViewClick(task.content || {})}
                        type="button"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {canManagePlan && (
        <PageSection
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          style={{ marginTop: '16px' }}
        >
          <StyledDeleteButton
            label="Delete Plan"
            width="120px"
            color="gray"
            onClick={async () => {
              const confirmed = window.confirm(
                `Are you sure you want to permanently delete this foundation action plan?`
              );

              if (!confirmed) return;

              try {
                await deleteActionPlan({ id: actionPlanId }).unwrap();
                addToast('Plan deleted successfully', {
                  appearance: 'success',
                  autoDismiss: true,
                });
              } catch (error) {
                console.error('Error deleting plan:', error);
              } finally {
                if (
                  actionPlanApi.util &&
                  typeof actionPlanApi.util.resetApiState === 'function'
                ) {
                  dispatch(actionPlanApi.util.resetApiState());
                }
                navigate(`/app/foundations/`);
              }
            }}
          />
        </PageSection>
      )}

      <ResourceListModal
        resources={resources || []}
        isOpen={isResourceListOpen}
        setModalOpen={setResourceListOpen}
      />

      {activeTaskId !== null && (
        <PositionedModal
          open={notesModalOpen}
          onClose={closeNotesModal}
          size="small"
          closeIcon
        >
          <Modal.Header>Task Notes</Modal.Header>
          <Modal.Content>
            <TextArea
              autoFocus
              style={{ width: '100%', height: '200px' }}
              value={
                editedTaskData[activeTaskId]?.notes !== undefined
                  ? (editedTaskData[activeTaskId]?.notes ?? '')
                  : (allTasks.find(t => t.id === activeTaskId)?.notes ?? '')
              }
              onChange={e => handleChange(activeTaskId, 'notes', e.target.value)}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={closeNotesModal} color="grey">
              Close
            </Button>
          </Modal.Actions>
        </PositionedModal>
      )}

      {/* Unsaved changes confirmation modal (replaces window.confirm) */}
      <PositionedModal open={unsavedModalOpen} onClose={() => setUnsavedModalOpen(false)} size="small">
        <Modal.Header>Unsaved changes</Modal.Header>
        <Modal.Content>
          You have unsaved changes. Are you sure you want to leave without saving?
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setUnsavedModalOpen(false)} color="grey">
            Stay
          </Button>
          <Button
            color="red"
            onClick={() => {
              setUnsavedModalOpen(false);
              // perform the pending navigation if any
              try {
                pendingPerformRef.current && pendingPerformRef.current();
              } finally {
                pendingPerformRef.current = null;
              }
            }}
          >
            Leave
          </Button>
        </Modal.Actions>
      </PositionedModal>
    </>
  );
};

export default FoundationActionPlan;

const Select = styled.select`
  color: ${tableCellColor};
  width: 100%;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const DividerTitle = styled.div`
  display: flex;
  flex-direction: row;
  text-align: space-between;
  justify-content: space-between;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 50px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;


